import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Copy, ExternalLink, Package, Info, Clock } from "lucide-react";
import { toast } from "sonner";
import { PersonFields, EMPTY_PERSON } from "./PersonFields";
import { copyText } from "./shippingMeta";

const STEPS = ["Dati", "Consegna", "Pacco", "Riepilogo", "InPost", "Tracking"];

function Stepper({ step }) {
  return (
    <ol className="mb-4 grid grid-cols-6 gap-1" data-testid="wizard-stepper">
      {STEPS.map((s, i) => (
        <li key={s} className="text-center">
          <div className={`mx-auto grid h-7 w-7 place-items-center rounded-full text-xs font-bold transition-colors ${i + 1 <= step ? "bg-[#7C3AED] text-white" : "bg-[#0B1020] text-[#64748B] border border-[#2A3655]"}`}>{i + 1}</div>
          <span className="mt-1 hidden text-[10px] text-[#94A3B8] sm:block">{s}</span>
        </li>
      ))}
    </ol>
  );
}

const personText = (d) => d ? [d.name, d.address, [d.cap, d.city, d.province && `(${d.province})`].filter(Boolean).join(" "), d.phone && `Tel. ${d.phone}`, d.email].filter(Boolean).join("\n") : "";

function CopyBlock({ title, text, testid }) {
  const copy = async () => { const ok = await copyText(text); ok ? toast.success(`${title} copiato`) : toast.error("Copia non riuscita"); };
  return (
    <div className="rounded-lg border border-[#2A3655] bg-[#0B1020] p-3">
      <div className="flex items-center justify-between gap-2"><p className="text-xs font-semibold text-[#94A3B8]">{title}</p>
        {text && <button type="button" onClick={copy} data-testid={testid} className="flex items-center gap-1 text-xs text-[#2DD4BF] hover:underline"><Copy className="h-3 w-3" />Copia</button>}</div>
      <p className="mt-1 whitespace-pre-line text-sm text-[#F8FAFC]">{text || "—"}</p>
    </div>
  );
}

export function ShippingWizard({ ship, cfg, cards, pid, open, onOpenChange, onChanged, onTracking }) {
  const w = ship.wizard || {};
  const [step, setStep] = useState(Math.min(w.step || 1, 6));
  const [sender, setSender] = useState({ ...EMPTY_PERSON, ...(ship.sender_data || {}) });
  const rd = ship.recipient_data;
  const [mode, setMode] = useState(w.delivery_mode || rd?.preferred_mode || "locker");
  const [point, setPoint] = useState(w.pickup_point_ref || rd?.pickup_point_ref || "");
  const [size, setSize] = useState(w.parcel_size || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (ship.sender_data) { return; }
    api.get(`/proposals/${pid}/delivery`).then((r) => {
      const m = r.data.mine;
      if (m) setSender((d) => ({ ...d, name: m.recipient_name || "", address: m.address || "", cap: m.cap || "", city: m.city || "", province: m.province || "", phone: m.phone || "" }));
    }).catch(() => {});
  }, [pid, ship.sender_data]);

  const save = async (next, extra) => {
    setBusy(true);
    try { await api.put(`/shipping/${ship.id}/wizard`, { step: next, ...extra }); setStep(next); onChanged(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const openInPost = async () => {
    window.open(cfg.official_url, "_blank", "noopener,noreferrer");
    try { await api.post(`/shipping/${ship.id}/inpost-opened`); setStep(6); onChanged(); } catch (e) { toast.error(apiError(e)); }
  };
  const modes = Object.entries(cfg.delivery_modes);
  const fmt = cfg.parcel_formats.find((f) => f.code === size);
  const cardsText = cards.map((c) => `${c.quantity}× ${c.name} (${c.condition}, ${c.language})`).join("\n");
  const pointText = mode !== "home" ? point : "";
  const back = step > 1 && step < 6 && <Button variant="outline" onClick={() => setStep(step - 1)} className="border-[#2A3655] bg-transparent text-[#F8FAFC] hover:bg-[#1C2640]" data-testid="wizard-back">Indietro</Button>;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto bg-[#141B2D] border-[#2A3655] text-[#F8FAFC] sm:max-w-2xl" data-testid="shipping-wizard">
        <DialogHeader>
          <DialogTitle className="font-display">Prepara la spedizione per @{ship.recipient_username}</DialogTitle>
          <DialogDescription className="text-[#94A3B8]">I dati restano salvati: puoi chiudere e riprendere quando vuoi.</DialogDescription>
        </DialogHeader>
        <Stepper step={step} />

        {step === 1 && (
          <div className="space-y-4" data-testid="wizard-step-1">
            <div><p className="mb-2 text-sm font-semibold text-[#2DD4BF]">Mittente (tu)</p><PersonFields value={sender} onChange={setSender} prefix="sender" /></div>
            <div><p className="mb-2 text-sm font-semibold text-[#7C3AED]">Destinatario</p>
              {rd ? <CopyBlock title="Dati confermati dal destinatario" text={personText(rd)} testid="copy-recipient-step1" />
                : <p className="flex items-center gap-2 rounded-lg border border-[#F59E0B]/30 bg-[#F59E0B]/10 p-3 text-xs text-[#F59E0B]" data-testid="wizard-waiting-recipient"><Clock className="h-4 w-4 shrink-0" />@{ship.recipient_username} non ha ancora confermato i dati di spedizione. Puoi proseguire; per acquistare su InPost serviranno i suoi dati.</p>}
            </div>
            <div className="flex justify-end"><Button onClick={() => save(2, { sender_data: sender })} disabled={busy} data-testid="wizard-next-1" className="bg-[#7C3AED] hover:bg-[#6D28D9]">Conferma e continua</Button></div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4" data-testid="wizard-step-2">
            <div className="grid gap-2 sm:grid-cols-3">
              {modes.map(([k, l]) => {
                const disabled = k === "home" && !cfg.home_delivery_available;
                return <button key={k} type="button" disabled={disabled} onClick={() => setMode(k)} data-testid={`wizard-mode-${k}`} className={`rounded-xl border p-3 text-left text-sm transition-colors ${mode === k ? "border-[#2DD4BF] bg-[#2DD4BF]/10 text-[#2DD4BF]" : "border-[#2A3655] text-[#CBD5E1]"} ${disabled ? "cursor-not-allowed opacity-40" : "hover:border-[#2DD4BF]/60"}`}>{l}{disabled && <span className="mt-1 block text-[10px] text-[#94A3B8]">Non disponibile nel servizio scelto</span>}</button>;
              })}
            </div>
            {mode !== "home" && (
              <div>
                <Label className="text-xs text-[#CBD5E1]">Riferimento del punto di ritiro del destinatario</Label>
                <Input value={point} onChange={(e) => setPoint(e.target.value)} placeholder="Codice o nome del Locker / Point" data-testid="wizard-point" className="bg-[#0B1020] border-[#2A3655]" />
                <p className="mt-1 text-[11px] text-[#94A3B8]">Omniverse non mostra elenchi di locker: il punto si sceglie sul sito InPost e qui se ne registra il riferimento.</p>
              </div>
            )}
            <div className="flex justify-between">{back}<Button onClick={() => save(3, { delivery_mode: mode, pickup_point_ref: point })} disabled={busy} data-testid="wizard-next-2" className="bg-[#7C3AED] hover:bg-[#6D28D9]">Continua</Button></div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4" data-testid="wizard-step-3">
            <div className="grid gap-2 sm:grid-cols-3">
              {cfg.parcel_formats.map((f) => (
                <button key={f.code} type="button" onClick={() => setSize(f.code)} data-testid={`wizard-size-${f.code}`} className={`rounded-xl border p-3 text-left transition-colors ${size === f.code ? "border-[#7C3AED] bg-[#7C3AED]/10" : "border-[#2A3655] hover:border-[#7C3AED]/60"}`}>
                  <Package className="h-5 w-5 text-[#7C3AED]" /><p className="mt-1 text-sm font-semibold text-[#F8FAFC]">{f.label}</p>
                  <p className="text-[11px] text-[#94A3B8]">max {f.max_dims_mm} mm · {f.max_weight_kg} kg</p>
                  <p className="mt-1 text-[11px] text-[#CBD5E1]">{f.price_note || "Prezzo confermato su InPost"}</p>
                </button>
              ))}
            </div>
            <p className="flex items-start gap-2 text-xs text-[#94A3B8]"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />Il costo effettivo sarà confermato durante l'acquisto su InPost. Misura il pacco già imballato. Per poche carte di solito basta la taglia più piccola.</p>
            <div className="flex justify-between">{back}<Button onClick={() => save(4, { parcel_size: size })} disabled={busy || !size} data-testid="wizard-next-3" className="bg-[#7C3AED] hover:bg-[#6D28D9]">Continua</Button></div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-3" data-testid="wizard-step-4">
            <div className="grid gap-2 sm:grid-cols-2">
              <CopyBlock title="Mittente" text={personText(ship.sender_data || sender)} testid="copy-sender" />
              <CopyBlock title="Destinatario" text={personText(rd)} testid="copy-recipient" />
              <CopyBlock title="Modalità di consegna" text={cfg.delivery_modes[mode]} testid="copy-mode" />
              <CopyBlock title="Formato del pacco" text={fmt ? `${fmt.label} – max ${fmt.max_dims_mm} mm` : ""} testid="copy-size" />
              {pointText && <CopyBlock title="Punto di ritiro" text={pointText} testid="copy-point" />}
              <CopyBlock title="Carte da spedire" text={cardsText} testid="copy-cards" />
            </div>
            <div className="flex justify-between">{back}<Button onClick={() => save(5, {})} disabled={busy} data-testid="wizard-next-4" className="bg-[#7C3AED] hover:bg-[#6D28D9]">Vai all'acquisto</Button></div>
          </div>
        )}

        {step === 5 && (
          <div className="space-y-4 text-center" data-testid="wizard-step-5">
            <p className="text-sm text-[#CBD5E1]">L'acquisto avviene sul sito ufficiale InPost Direct, in una nuova scheda: Omniverse non incassa denaro e non acquista per tuo conto. Usa i dati copiati dal riepilogo.</p>
            {!rd && <p className="text-xs text-[#F59E0B]" data-testid="wizard-step5-waiting">Aspetta che @{ship.recipient_username} confermi i propri dati prima di acquistare.</p>}
            <Button onClick={openInPost} disabled={!rd} size="lg" data-testid="open-inpost" className="bg-[#FFCD00] font-semibold text-[#111] hover:bg-[#f0c000]">Completa la spedizione su InPost <ExternalLink className="ml-2 h-4 w-4" /></Button>
            <div className="flex justify-start">{back}</div>
          </div>
        )}

        {step === 6 && (
          <div className="space-y-3 text-center" data-testid="wizard-step-6">
            <p className="text-sm text-[#CBD5E1]">Hai completato l'acquisto su InPost e consegnato il pacco? Registra il tracking per avvisare @{ship.recipient_username}.</p>
            <p className="text-xs text-[#94A3B8]">Finché non inserisci il tracking la spedizione resta "In attesa di spedizione".</p>
            <div className="flex flex-wrap justify-center gap-2">
              <Button variant="outline" onClick={() => window.open(cfg.official_url, "_blank", "noopener,noreferrer")} className="border-[#2A3655] bg-transparent text-[#F8FAFC] hover:bg-[#1C2640]" data-testid="reopen-inpost">Riapri InPost</Button>
              <Button onClick={() => { onOpenChange(false); onTracking(); }} data-testid="wizard-to-tracking" className="bg-[#7C3AED] hover:bg-[#6D28D9]">Inserisci tracking</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
